import type {
  NetworkResource,
  Relationship,
  MetricDataPoint,
  PaginatedResponse,
  SecurityFinding,
  Remediation,
  DashboardSummary,
  GraphData,
  Severity,
  ErrorResponse,
  Insight,
  ResourceHealthStatus,
  KpiData,
  ThresholdConfig,
  MetricResourceType,
  MetricType,
} from '@shared/types';

// ─── Response Types ────────────────────────────────────────────────────────────

/**
 * API envelope returned by the backend cache handler.
 * Contains the data plus optional staleness indicators.
 */
export interface ApiResponse<T> {
  data: T;
  stale: boolean;
  cachedAt: string | null;
}

/**
 * Sync status as returned by GET /api/sync/status.
 */
export interface SyncStatus {
  status: 'running' | 'completed' | 'failed' | 'never';
  lastSyncAt: string | null;
  startedAt?: string;
  completedAt?: string | null;
  resourcesDiscovered?: number;
  errors?: unknown[];
}

/**
 * Security summary grouped by severity.
 */
export interface SecuritySummary {
  critical: number;
  high: number;
  medium: number;
  low: number;
  totalEvaluated: number;
}

/**
 * Finding detail includes remediations list and resource context.
 */
export interface SecurityFindingDetail extends SecurityFinding {
  remediations: Remediation[];
  resourceName: string | null;
  resourceType: string | null;
  projectOrAccount: string | null;
  region: string | null;
  provider: string | null;
  consoleUrl: string | null;
}

// ─── Error Handling ────────────────────────────────────────────────────────────

/**
 * Custom error thrown when the API returns an error response.
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details?: Record<string, unknown>;

  constructor(status: number, errorBody: ErrorResponse) {
    super(errorBody.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = errorBody.code;
    this.details = errorBody.details;
  }
}

// ─── Auth Redirect ─────────────────────────────────────────────────────────────

/**
 * Triggers Cloudflare Access login by reloading the page.
 * Cloudflare Access will intercept the request and redirect to Azure AD.
 */
function triggerAuthRedirect(): never {
  window.location.href = '/cdn-cgi/access/logout';
  // This line won't be reached but satisfies TypeScript's `never` return type.
  throw new Error('Redirecting to login...');
}

// ─── Fetch Wrapper ─────────────────────────────────────────────────────────────

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

/**
 * Typed fetch wrapper that:
 * - Sends CF_Authorization cookie via `credentials: 'include'`
 * - Handles 401 by triggering Cloudflare Access auth redirect
 * - Parses the API envelope (data + stale + cachedAt)
 * - Throws ApiError on non-2xx responses
 */
async function apiFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${path}`;
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  // Handle 401 — session expired or unauthenticated
  if (response.status === 401) {
    triggerAuthRedirect();
  }

  const body = await response.json();

  // Handle error responses
  if (!response.ok) {
    const errorBody = (body as { error?: ErrorResponse }).error ?? {
      code: `HTTP_${response.status}`,
      message: response.statusText || 'An error occurred',
    };
    throw new ApiError(response.status, errorBody);
  }

  // The API worker wraps responses in { data, stale, cachedAt }
  // But some routes may return data directly without the envelope
  const envelope = body as Record<string, unknown>;
  
  if ('stale' in envelope && 'cachedAt' in envelope) {
    // Response has cache envelope
    return {
      data: envelope.data as T,
      stale: (envelope.stale as boolean) ?? false,
      cachedAt: (envelope.cachedAt as string) ?? null,
    };
  }
  
  // No envelope — response IS the data directly
  return {
    data: body as T,
    stale: false,
    cachedAt: null,
  };
}

// ─── Query Parameter Helpers ───────────────────────────────────────────────────

interface ResourceListParams {
  provider?: string;
  type?: string;
  region?: string;
  project?: string;
  search?: string;
  severity?: string;
  page?: number;
  pageSize?: number;
}

interface FindingsListParams {
  severity?: Severity;
  provider?: string;
  type?: string;
  region?: string;
  project?: string;
  environment?: string;
  page?: number;
  pageSize?: number;
}

type MetricRange = '15m' | '30m' | '1h' | '6h' | '12h' | '24h' | '7d' | '30d';

function buildQueryString(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== ''
  );
  if (entries.length === 0) return '';
  const searchParams = new URLSearchParams();
  for (const [key, value] of entries) {
    searchParams.set(key, String(value));
  }
  return `?${searchParams.toString()}`;
}

// ─── Exported API Functions ────────────────────────────────────────────────────

/**
 * GET /api/resources/filter-options — available filter values from discovered resources.
 * Supports cascading filters: pass current filter selections so each dimension
 * only shows values that exist given the other active filters.
 */
export interface FilterOptions {
  regions: string[];
  resourceTypes: string[];
  projectsOrAccounts: string[];
  providers: string[];
  environments: string[];
}

export interface FilterOptionsParams {
  provider?: string;
  type?: string;
  region?: string;
  project?: string;
  environment?: string;
}

export async function getFilterOptions(params?: FilterOptionsParams): Promise<ApiResponse<FilterOptions>> {
  const qs = params ? buildQueryString(params) : '';
  return apiFetch<FilterOptions>(`/api/resources/filter-options${qs}`);
}

/**
 * GET /api/resources — paginated resource list with optional filters.
 */
export async function getResources(
  params: ResourceListParams = {}
): Promise<ApiResponse<PaginatedResponse<NetworkResource>>> {
  const qs = buildQueryString(params);
  return apiFetch<PaginatedResponse<NetworkResource>>(`/api/resources${qs}`);
}

/**
 * GET /api/resources/:id — single resource detail.
 */
export async function getResource(id: string): Promise<ApiResponse<NetworkResource>> {
  return apiFetch<NetworkResource>(`/api/resources/${encodeURIComponent(id)}`);
}

/**
 * GET /api/resources/:id/relationships — relationships for a resource.
 */
export async function getResourceRelationships(
  id: string
): Promise<ApiResponse<Relationship[]>> {
  return apiFetch<Relationship[]>(
    `/api/resources/${encodeURIComponent(id)}/relationships`
  );
}

/**
 * GET /api/resources/:id/metrics — metrics for a resource with time range.
 */
export async function getResourceMetrics(
  id: string,
  range: MetricRange = '1h'
): Promise<ApiResponse<MetricDataPoint[]>> {
  return apiFetch<MetricDataPoint[]>(
    `/api/resources/${encodeURIComponent(id)}/metrics?range=${range}`
  );
}

/**
 * GET /api/relationships — graph data with optional filters.
 */
export async function getRelationships(
  params: { provider?: string; type?: string; project?: string } = {}
): Promise<ApiResponse<GraphData>> {
  const qs = buildQueryString(params);
  return apiFetch<GraphData>(`/api/relationships${qs}`);
}

/**
 * GET /api/security/summary — security findings count by severity.
 */
export async function getSecuritySummary(): Promise<ApiResponse<SecuritySummary>> {
  return apiFetch<SecuritySummary>('/api/security/summary');
}

/**
 * GET /api/security/findings — paginated security findings list.
 */
export async function getSecurityFindings(
  params: FindingsListParams = {}
): Promise<ApiResponse<PaginatedResponse<SecurityFinding>>> {
  const qs = buildQueryString(params);
  return apiFetch<PaginatedResponse<SecurityFinding>>(
    `/api/security/findings${qs}`
  );
}

/**
 * GET /api/security/findings/:id — single finding detail with remediations.
 */
export async function getSecurityFinding(
  id: string
): Promise<ApiResponse<SecurityFindingDetail>> {
  return apiFetch<SecurityFindingDetail>(
    `/api/security/findings/${encodeURIComponent(id)}`
  );
}

/**
 * PATCH /api/security/findings/:findingId/remediations/:remediationId
 * Mark a remediation as completed.
 */
export async function markRemediationCompleted(
  findingId: string,
  remediationId: string
): Promise<ApiResponse<void>> {
  return apiFetch<void>(
    `/api/security/findings/${encodeURIComponent(findingId)}/remediations/${encodeURIComponent(remediationId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ status: 'completed' }),
    }
  );
}

/**
 * GET /api/dashboard/summary — dashboard summary data.
 */
export async function getDashboardSummary(): Promise<ApiResponse<DashboardSummary>> {
  return apiFetch<DashboardSummary>('/api/dashboard/summary');
}

/**
 * GET /api/metrics/dashboard — aggregated metrics for the dashboard.
 */
export async function getMetricsDashboard(
  range: MetricRange = '1h',
  start?: string,
  end?: string
): Promise<ApiResponse<MetricDataPoint[]>> {
  if (start && end) {
    return apiFetch<MetricDataPoint[]>(`/api/metrics/dashboard?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`);
  }
  return apiFetch<MetricDataPoint[]>(`/api/metrics/dashboard?range=${range}`);
}

/**
 * GET /api/sync/status — last sync status info.
 */
export async function getSyncStatus(): Promise<ApiResponse<SyncStatus>> {
  return apiFetch<SyncStatus>('/api/sync/status');
}


/**
 * GET /api/dashboard/insights — dynamic insights and recommendations.
 */
export async function getDashboardInsights(
  range: MetricRange = '1h'
): Promise<ApiResponse<Insight[]>> {
  return apiFetch<Insight[]>(`/api/dashboard/insights?range=${range}`);
}

// ─── Metrics Dashboard Pro API Functions ───────────────────────────────────────

interface TimeSeriesParams {
  time_range?: MetricRange;
  resource_type?: MetricResourceType;
  resource_id?: string;
  metric_type?: MetricType;
  page?: number;
  pageSize?: number;
  start?: string;
  end?: string;
  aggregate?: 'sum' | 'avg';
}

/**
 * GET /api/metrics/timeseries — time-series metric data with filters and pagination.
 */
export async function fetchTimeSeries(
  params: TimeSeriesParams = {}
): Promise<ApiResponse<PaginatedResponse<MetricDataPoint>>> {
  const qs = buildQueryString(params as Record<string, string | number | undefined>);
  return apiFetch<PaginatedResponse<MetricDataPoint>>(`/api/metrics/timeseries${qs}`);
}

interface HealthParams {
  resource_type?: MetricResourceType;
  region?: string;
  project?: string;
  environment?: string;
}

/**
 * GET /api/metrics/health — health status for all monitored resources.
 */
export async function fetchHealth(
  params: HealthParams = {}
): Promise<ApiResponse<ResourceHealthStatus[]>> {
  const qs = buildQueryString(params as Record<string, string | number | undefined>);
  return apiFetch<ResourceHealthStatus[]>(`/api/metrics/health${qs}`);
}

/**
 * GET /api/metrics/kpi — aggregated KPI data for the overview dashboard.
 */
export async function fetchKpi(
  timeRange?: MetricRange
): Promise<ApiResponse<KpiData>> {
  const qs = timeRange ? `?time_range=${timeRange}` : '';
  return apiFetch<KpiData>(`/api/metrics/kpi${qs}`);
}

// ─── Monitoring-dedicated endpoints (/api/monitoring/*) ───────────────────────

/**
 * GET /api/monitoring/health — health status from dedicated monitoring data.
 */
export async function fetchMonitoringHealth(
  params: HealthParams = {}
): Promise<ApiResponse<ResourceHealthStatus[]>> {
  const qs = buildQueryString(params as Record<string, string | number | undefined>);
  return apiFetch<ResourceHealthStatus[]>(`/api/monitoring/health${qs}`);
}

/**
 * GET /api/monitoring/timeseries — time-series data from dedicated monitoring table.
 */
export async function fetchMonitoringTimeSeries(
  params: TimeSeriesParams = {}
): Promise<ApiResponse<PaginatedResponse<MetricDataPoint>>> {
  const qs = buildQueryString(params as Record<string, string | number | undefined>);
  return apiFetch<PaginatedResponse<MetricDataPoint>>(`/api/monitoring/timeseries${qs}`);
}

/**
 * GET /api/monitoring/kpi — KPI aggregates from dedicated monitoring data.
 */
export async function fetchMonitoringKpi(
  timeRange?: MetricRange
): Promise<ApiResponse<KpiData>> {
  const qs = timeRange ? `?time_range=${timeRange}` : '';
  return apiFetch<KpiData>(`/api/monitoring/kpi${qs}`);
}

/**
 * GET /api/thresholds — retrieve all threshold configurations.
 */
export async function fetchThresholds(): Promise<ApiResponse<ThresholdConfig[]>> {
  return apiFetch<ThresholdConfig[]>('/api/thresholds');
}

/**
 * PUT /api/thresholds/:resourceType/:metricType — update a specific threshold.
 */
export async function updateThreshold(
  resourceType: MetricResourceType,
  metricType: MetricType,
  body: Omit<ThresholdConfig, 'resourceType' | 'metricType'>
): Promise<ApiResponse<ThresholdConfig>> {
  return apiFetch<ThresholdConfig>(
    `/api/thresholds/${encodeURIComponent(resourceType)}/${encodeURIComponent(metricType)}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
    }
  );
}

/**
 * GET /api/dashboard/insights — alias for getDashboardInsights.
 * Provides a consistent naming pattern with the other fetch* methods.
 */
export const fetchInsights = getDashboardInsights;

/**
 * POST /api/dashboard/insights/dismiss — dismiss a specific insight for 24 hours.
 */
export async function dismissInsight(
  insightId: string
): Promise<ApiResponse<void>> {
  return apiFetch<void>('/api/dashboard/insights/dismiss', {
    method: 'POST',
    body: JSON.stringify({ insightId }),
  });
}
